import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS = {
  aberto: { label: "Aberto", cls: "bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300" },
  em_andamento: { label: "Em andamento", cls: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300" },
  aguardando_cliente: { label: "Aguardando cliente", cls: "bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300" },
  resolvido: { label: "Resolvido", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300" },
  fechado: { label: "Fechado", cls: "bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200" },
};
const PRIORITY_LABELS = {
  baixa: { label: "Baixa", cls: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300" },
  media: { label: "Média", cls: "bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300" },
  alta: { label: "Alta", cls: "bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300" },
  urgente: { label: "Urgente", cls: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300" },
};

export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "media", category: "Geral" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/tickets");
      setTickets(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tickets", form);
      toast.success("Chamado criado!");
      setOpen(false);
      setForm({ title: "", description: "", priority: "media", category: "Geral" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = tickets.filter((t) => {
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase())
      || (t.description || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-8" data-testid="tickets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Suporte</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Chamados</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {user.role === "cliente" ? "Seus chamados abertos e histórico." : "Todos os chamados do sistema."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-ticket-btn"><Plus className="mr-2 h-4 w-4" /> Novo chamado</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Abrir chamado</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label htmlFor="t-title">Título</Label>
                <Input id="t-title" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="ticket-title-input" />
              </div>
              <div>
                <Label htmlFor="t-cat">Categoria</Label>
                <Input id="t-cat" value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="ticket-category-input" />
              </div>
              <div>
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="t-desc">Descrição</Label>
                <Textarea id="t-desc" required rows={5} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="ticket-description-input" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="ticket-submit-btn">
                  {saving ? "Salvando..." : "Criar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por título ou descrição..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9"
            data-testid="ticket-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52" data-testid="ticket-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="aberto">Aberto</SelectItem>
            <SelectItem value="em_andamento">Em andamento</SelectItem>
            <SelectItem value="aguardando_cliente">Aguardando cliente</SelectItem>
            <SelectItem value="resolvido">Resolvido</SelectItem>
            <SelectItem value="fechado">Fechado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead className="hidden md:table-cell">Categoria</TableHead>
              <TableHead>Prioridade</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Solicitante</TableHead>
              <TableHead className="hidden lg:table-cell">Responsável</TableHead>
              <TableHead className="hidden md:table-cell">Aberto em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                Nenhum chamado encontrado.
              </TableCell></TableRow>
            ) : filtered.map((t) => (
              <TableRow
                key={t.id}
                className="cursor-pointer hover:bg-accent"
                onClick={() => navigate(`/tickets/${t.id}`)}
                data-testid={`ticket-row-${t.id}`}
              >
                <TableCell className="font-medium">{t.title}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{t.category}</TableCell>
                <TableCell><Badge className={PRIORITY_LABELS[t.priority]?.cls}>{PRIORITY_LABELS[t.priority]?.label}</Badge></TableCell>
                <TableCell><Badge className={STATUS_LABELS[t.status]?.cls}>{STATUS_LABELS[t.status]?.label}</Badge></TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground">{t.created_by_name}</TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground">{t.assignee_name || "—"}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground text-xs">
                  {new Date(t.created_at).toLocaleString("pt-BR")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
